import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleAPI, accidentAPI } from '../api/client';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Camera, Video, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const PHOTO_CHECKLIST = [
  { label: 'Overall Scene - Front View', category: 'scene' },
  { label: 'Overall Scene - Rear View', category: 'scene' },
  { label: 'Overall Scene - Left Side', category: 'scene' },
  { label: 'Overall Scene - Right Side', category: 'scene' },
  { label: 'Your Vehicle - Front Damage', category: 'your_vehicle' },
  { label: 'Your Vehicle - Rear Damage', category: 'your_vehicle' },
  { label: 'Your Vehicle - Left Side Damage', category: 'your_vehicle' },
  { label: 'Your Vehicle - Right Side Damage', category: 'your_vehicle' },
  { label: 'Other Party - License Plate', category: 'other_party' },
  { label: 'Other Party - Vehicle Damage', category: 'other_party' },
  { label: 'Road Conditions & Signage', category: 'conditions' },
  { label: 'Skid Marks or Debris', category: 'conditions' },
];

const CreateAccident = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [vehicles, setVehicles] = useState([]);
  const [reportId, setReportId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    accident_date: '',
    accident_time: '',
    location: '',
    weather_conditions: '',
    road_conditions: '',
    description: '',
    statement: '',
    other_party: {
      name: '',
      phone: '',
      vehicle_reg: '',
      insurance: ''
    },
    witnesses: [],
    police_report_number: '',
    injuries: ''
  });
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [videoFile, setVideoFile] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleAPI.list();
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      // Create accident report
      if (!formData.vehicle_id || !formData.accident_date || !formData.accident_time || !formData.location) {
        toast.error('Please fill all required fields');
        return;
      }
      setLoading(true);
      try {
        const response = await accidentAPI.create(formData);
        setReportId(response.data.report_id);
        toast.success('Accident report created');
        setStep(step + 1);
      } catch (error) {
        toast.error('Failed to create report');
      } finally {
        setLoading(false);
      }
    } else if (step < 5) {
      setStep(step + 1);
    }
  };

  const handlePhotoUpload = async (label, file) => {
    if (!reportId) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', label);
    try {
      await accidentAPI.uploadPhoto(reportId, formData);
      setUploadedPhotos([...uploadedPhotos, label]);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async () => {
    if (!reportId || !videoFile) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', videoFile);
    try {
      await accidentAPI.uploadVideo(reportId, formData);
      toast.success('Video uploaded');
      setStep(step + 1);
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      await accidentAPI.generatePDF(reportId);
      toast.success('PDF report generated!');
      navigate(`/app/accidents/${reportId}`);
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / 5) * 100;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/app/accidents')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Accidents
        </Button>
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-destructive/10 p-2 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h1 className="text-4xl font-semibold tracking-tight">Report Accident</h1>
        </div>
        <p className="text-muted-foreground">Step {step} of 5: Follow our guided process to collect all evidence</p>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Basic Details</span>
          <span>Photos</span>
          <span>Video</span>
          <span>Parties & Witnesses</span>
          <span>Review & PDF</span>
        </div>
      </div>

      {/* Step 1: Basic Details */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Accident Details</CardTitle>
            <CardDescription>Basic information about the accident</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Vehicle *</Label>
              <Select value={formData.vehicle_id} onValueChange={(value) => setFormData({...formData, vehicle_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                      {v.year} {v.make} {v.model} ({v.reg_plate})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={formData.accident_date} onChange={(e) => setFormData({...formData, accident_date: e.target.value})} />
              </div>
              <div>
                <Label>Time *</Label>
                <Input type="time" value={formData.accident_time} onChange={(e) => setFormData({...formData, accident_time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Location *</Label>
              <Input placeholder="e.g., M25 Junction 12, London" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Weather Conditions</Label>
                <Select value={formData.weather_conditions} onValueChange={(value) => setFormData({...formData, weather_conditions: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select weather" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clear">Clear</SelectItem>
                    <SelectItem value="rain">Rain</SelectItem>
                    <SelectItem value="fog">Fog</SelectItem>
                    <SelectItem value="snow">Snow</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Road Conditions</Label>
                <Select value={formData.road_conditions} onValueChange={(value) => setFormData({...formData, road_conditions: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select conditions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dry">Dry</SelectItem>
                    <SelectItem value="wet">Wet</SelectItem>
                    <SelectItem value="icy">Icy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Brief Description</Label>
              <Textarea placeholder="What happened?" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Photo Upload */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Photograph Evidence</CardTitle>
            <CardDescription>Capture photos from these recommended angles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {PHOTO_CHECKLIST.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Camera className="h-5 w-5 text-muted-foreground" />
                    <span>{item.label}</span>
                  </div>
                  {uploadedPhotos.includes(item.label) ? (
                    <Check className="h-5 w-5 text-success" />
                  ) : (
                    <Input
                      type="file"
                      accept="image/*"
                      className="max-w-[200px]"
                      onChange={(e) => e.target.files[0] && handlePhotoUpload(item.label, e.target.files[0])}
                    />
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Tip: You can skip photos and add them later. Upload at least the most important ones.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Video */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Scene Video (Optional)</CardTitle>
            <CardDescription>Record a video walkthrough of the accident scene (max 50MB)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center p-12 border-2 border-dashed rounded-lg">
              <div className="text-center">
                <Video className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <Input
                  type="file"
                  accept="video/mp4,video/quicktime"
                  onChange={(e) => setVideoFile(e.target.files[0])}
                  className="max-w-xs mx-auto"
                />
                <p className="text-sm text-muted-foreground mt-2">MP4 or MOV format, max 50MB</p>
                {videoFile && (
                  <p className="text-sm text-success mt-2">Selected: {videoFile.name}</p>
                )}
              </div>
            </div>
            {videoFile && (
              <Button onClick={handleVideoUpload} disabled={loading} className="w-full">
                {loading ? 'Uploading...' : 'Upload Video & Continue'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Parties & Statement */}
      {step === 4 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Other Party Details</CardTitle>
              <CardDescription>Information about the other driver involved</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input placeholder="Name" value={formData.other_party.name} onChange={(e) => setFormData({...formData, other_party: {...formData.other_party, name: e.target.value}})} />
              <Input placeholder="Phone" value={formData.other_party.phone} onChange={(e) => setFormData({...formData, other_party: {...formData.other_party, phone: e.target.value}})} />
              <Input placeholder="Vehicle Registration" value={formData.other_party.vehicle_reg} onChange={(e) => setFormData({...formData, other_party: {...formData.other_party, vehicle_reg: e.target.value}})} />
              <Input placeholder="Insurance Company" value={formData.other_party.insurance} onChange={(e) => setFormData({...formData, other_party: {...formData.other_party, insurance: e.target.value}})} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your Statement</CardTitle>
              <CardDescription>Describe what happened in your own words</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea placeholder="I was traveling northbound when..." value={formData.statement} onChange={(e) => setFormData({...formData, statement: e.target.value})} rows={6} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Police Report Number (if applicable)</Label>
                <Input placeholder="POL-2025-061523" value={formData.police_report_number} onChange={(e) => setFormData({...formData, police_report_number: e.target.value})} />
              </div>
              <div>
                <Label>Injuries (if any)</Label>
                <Textarea placeholder="Describe any injuries" value={formData.injuries} onChange={(e) => setFormData({...formData, injuries: e.target.value})} rows={2} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 5: Review & Generate PDF */}
      {step === 5 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Generate PDF</CardTitle>
            <CardDescription>Your accident report is ready</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
              <Check className="h-8 w-8 text-success" />
              <div>
                <p className="font-semibold">Report Complete</p>
                <p className="text-sm text-muted-foreground">All evidence has been collected</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <p>✅ Basic accident details recorded</p>
              <p>✅ {uploadedPhotos.length} photos uploaded</p>
              {videoFile && <p>✅ Scene video recorded</p>}
              <p>✅ Statement provided</p>
            </div>
            <Button onClick={handleGeneratePDF} disabled={loading} className="w-full" size="lg">
              <FileText className="h-5 w-5 mr-2" />
              {loading ? 'Generating PDF...' : 'Generate PDF Report'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1 || loading}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        {step < 5 && (
          <Button onClick={handleNext} disabled={loading}>
            {loading ? 'Loading...' : 'Next'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateAccident;
